export const MONTHS_BILLED_PER_ANNUAL_SELF_SERVE = 10 as const

export const PLAN_IDS = ["free", "pro", "business", "enterprise"] as const

export type PlanId = (typeof PLAN_IDS)[number]

export const PAID_SELF_SERVE_PLAN_IDS = ["pro", "business"] as const

export type PaidSelfServePlanId = (typeof PAID_SELF_SERVE_PLAN_IDS)[number]

export type BillingInterval = "monthly" | "annual"

export const DEFAULT_PLAN_FOR_NEW_PROFILE: PlanId = "free"

export function isSelfServePaidPlan(plan: PlanId): plan is PaidSelfServePlanId {
  return plan === "pro" || plan === "business"
}

const PLAN_SET = new Set<string>(PLAN_IDS)

export function isPlanId(value: unknown): value is PlanId {
  return typeof value === "string" && PLAN_SET.has(value)
}

export class InvalidStoredPlanIdError extends Error {
  readonly raw: string

  constructor(raw: string) {
    super(`Invalid billing plan id: ${raw || "(empty)"}`)
    this.name = "InvalidStoredPlanIdError"
    this.raw = raw
  }
}

export function requirePlanId(value: string | null | undefined): PlanId {
  const s = typeof value === "string" ? value.trim() : ""
  if (isPlanId(s)) return s
  throw new InvalidStoredPlanIdError(s)
}

export interface PlanEntitlements {
  maxActiveEvents: number
  maxMediaItems: number
  maxVideos: number
  maxStorageBytes: number
  maxSmsPerMonth: number
  realtimeEnabled: boolean
  retentionPeriodDays: number
  entitlementsAreCustom: boolean
}

export interface PlanFeatureFlags {
  realtimeCoPresence: boolean
  smsOutbound: boolean
}

export interface PlanStorageAddOn {
  chunkSizeBytes: number
  maxSelfServeChunks: number
}

export interface PlanDefinition {
  id: PlanId
  displayName: string
  monthlyPriceCents: number | null
  annualPriceCents: number | null
  selfServe: boolean
  featureFlags: PlanFeatureFlags
  entitlements: PlanEntitlements
  storageAddOn: PlanStorageAddOn
}

function annualFromMonthly(monthly: number | null): number | null {
  if (monthly == null) return null
  return monthly * MONTHS_BILLED_PER_ANNUAL_SELF_SERVE
}

const GIB = 1024 * 1024 * 1024

export const PLAN_CATALOG: Record<PlanId, PlanDefinition> = {
  free: {
    id: "free",
    displayName: "Free",
    monthlyPriceCents: 0,
    annualPriceCents: null,
    selfServe: true,
    featureFlags: { realtimeCoPresence: false, smsOutbound: false },
    entitlements: {
      maxActiveEvents: 25,
      maxMediaItems: 500,
      maxVideos: 0,
      maxStorageBytes: 2 * GIB,
      maxSmsPerMonth: 0,
      realtimeEnabled: false,
      retentionPeriodDays: 7,
      entitlementsAreCustom: false,
    },
    storageAddOn: { chunkSizeBytes: 5 * GIB, maxSelfServeChunks: 0 },
  },
  pro: {
    id: "pro",
    displayName: "Pro",
    monthlyPriceCents: 800,
    annualPriceCents: annualFromMonthly(800),
    selfServe: true,
    featureFlags: { realtimeCoPresence: true, smsOutbound: true },
    entitlements: {
      maxActiveEvents: 25,
      maxMediaItems: 120,
      maxVideos: 5,
      maxStorageBytes: 25 * GIB,
      maxSmsPerMonth: 50,
      realtimeEnabled: true,
      retentionPeriodDays: 365,
      entitlementsAreCustom: false,
    },
    storageAddOn: { chunkSizeBytes: 10 * GIB, maxSelfServeChunks: 4 },
  },
  business: {
    id: "business",
    displayName: "Business",
    monthlyPriceCents: 1900,
    annualPriceCents: annualFromMonthly(1900),
    selfServe: true,
    featureFlags: { realtimeCoPresence: true, smsOutbound: true },
    entitlements: {
      maxActiveEvents: 500,
      maxMediaItems: 2000,
      maxVideos: 50,
      maxStorageBytes: 100 * GIB,
      maxSmsPerMonth: 500,
      realtimeEnabled: true,
      retentionPeriodDays: 1825,
      entitlementsAreCustom: false,
    },
    storageAddOn: { chunkSizeBytes: 50 * GIB, maxSelfServeChunks: 8 },
  },
  enterprise: {
    id: "enterprise",
    displayName: "Enterprise",
    monthlyPriceCents: null,
    annualPriceCents: null,
    selfServe: false,
    featureFlags: { realtimeCoPresence: true, smsOutbound: true },
    entitlements: {
      maxActiveEvents: -1,
      maxMediaItems: -1,
      maxVideos: -1,
      maxStorageBytes: -1,
      maxSmsPerMonth: -1,
      realtimeEnabled: true,
      retentionPeriodDays: 3650,
      entitlementsAreCustom: true,
    },
    storageAddOn: { chunkSizeBytes: 100 * GIB, maxSelfServeChunks: 0 },
  },
}

export function getPlanDefinition(plan: PlanId): PlanDefinition {
  return PLAN_CATALOG[plan]
}

export function getEntitlementsForPlan(plan: PlanId): PlanEntitlements {
  return PLAN_CATALOG[plan].entitlements
}

export function listPlanDefinitionsOrdered(): readonly PlanDefinition[] {
  return PLAN_IDS.map((id) => PLAN_CATALOG[id])
}

/** Effective per-month cost when paying annual prepay (catalog total ÷ 12). */
export function planAnnualEffectiveMonthlyCents(
  plan: PlanDefinition,
): number | null {
  const a = plan.annualPriceCents
  if (a == null || !Number.isFinite(a) || a <= 0) return null
  return Math.round(a / 12)
}

/** Savings vs paying list monthly twelve times (same basis as catalog annual). */
export function planAnnualSavingsVsTwelveMonthlyPaymentsCents(
  plan: PlanDefinition,
): number | null {
  const m = plan.monthlyPriceCents
  const a = plan.annualPriceCents
  if (m == null || !Number.isFinite(m) || m <= 0) return null
  if (a == null || !Number.isFinite(a) || a <= 0) return null
  return m * 12 - a
}
